import {
  Box,
  Flex,
  Spacer,
  Skeleton,
  useToast,
  Card,
  CardBody,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { CoreBorrowingBaseInsightsGqlDataGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
  ChartTimePeriod,
  CoreBorrowingBaseInsightsKeys,
  defaultSelectedTimePeriod,
  formatChartDataByReportingDate,
  formatCsvDownloadDataRows,
} from '../shared/portfolioChartUtils';
import { PortfolioTimePeriodSelectButtons } from '../shared/PortfolioTimePeriodSelectButtons';
import { PortfolioSummaryChart } from './PortfolioSummaryChart';
import { PortfolioSummaryChartCards } from './PortfolioSummaryChartCards';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { useGetDailyCoreBorrowingBaseInsightsQuery } from '~/packages/portfolio/requests/requests.gql.generated';
import { PortfolioDownloadCsvLink } from '~/packages/portfolio/ui/shared/PortfolioDownloadCsvLink';

const portfolioSummaryChartFields: CoreBorrowingBaseInsightsKeys[] = [
  'reportingDate',
  'commitmentAmount',
  'currentPrincipalBalance',
  'totalAssets',
  'totalBorrowingBaseAmount',
];

type PortfolioSummaryChartContainerProps = {
  creditFacilityId: string;
};
export const PortfolioSummaryChartContainer = ({
  creditFacilityId,
}: PortfolioSummaryChartContainerProps) => {
  const toast = useToast();
  const [
    selectedBar,
    setSelectedBar,
  ] = useState<CoreBorrowingBaseInsightsGqlDataGQLType | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataForDateRange, setChartDataForDateRange] = useState<
    CoreBorrowingBaseInsightsGqlDataGQLType[]
  >([]);
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    CoreBorrowingBaseInsightsGqlDataGQLType[]
  >([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );

  const {
    refetch: refetchBorrowingBaseInsightsData,
    data: borrowingBaseInsightsData,
    loading: borrowingBaseInsightsLoading,
  } = useGetDailyCoreBorrowingBaseInsightsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
    onCompleted: (data) => {
      // we must make sure to set the core date range chart data with all of the data by day
      // this ensures that we have the base by-day data ready to use
      // when going back to a higher-frequency time period view than the currently selected period
      const formattedChartData = formatChartDataByReportingDate({
        chartData: data.getDailyCoreBorrowingBaseInsights,
        selectedTimePeriod: ChartTimePeriod.day,
      });
      setChartDataForDateRange(formattedChartData);

      // then set data for the specific time period
      if (selectedTimePeriod !== ChartTimePeriod.day) {
        const formattedChartDataForPeriod = formatChartDataByReportingDate({
          chartData: data.getDailyCoreBorrowingBaseInsights,
          selectedTimePeriod,
        });
        setChartDataWithTimePeriod(formattedChartDataForPeriod);
      } else {
        setChartDataWithTimePeriod(formattedChartData);
      }

      setSelectedBar(formattedChartData[formattedChartData.length - 1]);
    },
    notifyOnNetworkStatusChange: true,
  });

  const { dataRows, headers } = useMemo(() => {
    return formatCsvDownloadDataRows<CoreBorrowingBaseInsightsGqlDataGQLType>(
      chartDataWithTimePeriod,
      portfolioSummaryChartFields,
    );
  }, [chartDataWithTimePeriod]);

  if (borrowingBaseInsightsLoading || !borrowingBaseInsightsData) {
    return <Skeleton height="600px" />;
  }

  const onSelectDateRange = (newDateRange: [Date | null, Date | null]) => {
    const [newStartDate, newEndDate] = newDateRange;

    if (newStartDate && newEndDate) {
      refetchBorrowingBaseInsightsData({
        creditFacilityParams: {
          creditFacilityId,
          reportingDateStart: newStartDate
            ? commonDate.dateToFullDateString(newStartDate)
            : getDefaultDashboardDisplayStartDate(),
          reportingDateEnd: newEndDate
            ? commonDate.dateToFullDateString(newEndDate)
            : getDefaultDashboardDisplayEndDate(),
        },
      });
      setChartDataDateRange({
        chartDataStartDate: newStartDate,
        chartDataEndDate: newEndDate,
      });
    }

    // if dates cleared, pass in the initial reporting date conditions
    if (!newStartDate && !newEndDate) {
      refetchBorrowingBaseInsightsData({
        creditFacilityParams: {
          creditFacilityId,
          reportingDateStart: getDefaultDashboardDisplayStartDate(),
          reportingDateEnd: getDefaultDashboardDisplayEndDate(),
        },
      });
      setChartDataDateRange({
        chartDataStartDate: chartsInitialStartDateJs,
        chartDataEndDate: chartsInitialEndDateJs,
      });
    }
  };

  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const formattedChartData = formatChartDataByReportingDate({
      chartData: chartDataForDateRange,
      selectedTimePeriod: timePeriod,
    });

    setChartDataWithTimePeriod(formattedChartData);
  };

  const onSelectBar = (
    selectedBarData: CoreBorrowingBaseInsightsGqlDataGQLType,
  ) => {
    setSelectedBar(selectedBarData);
  };

  return (
    <Flex direction="column" mb="40px" gap="1.5rem">
      <Typography variant="section-title" trim>
        Funding Overview
      </Typography>
      <Card variant="outline" size="lg">
        <CardBody>
          <Flex>
            <FullDateRangePicker
              onSelectDateRange={onSelectDateRange}
              currentDateRange={{
                startDate: chartDataDateRange.chartDataStartDate,
                endDate: chartDataDateRange.chartDataEndDate,
              }}
              w="lg"
            />
            <Spacer />
            <PortfolioTimePeriodSelectButtons
              currentSelectedTimePeriod={selectedTimePeriod}
              onSelectTimePeriod={onSelectTimePeriod}
            />
          </Flex>
          <Box mt="40px">
            <PortfolioSummaryChart
              chartData={chartDataWithTimePeriod}
              selectedTimePeriod={selectedTimePeriod}
              currentSelectedBarReportingDate={
                selectedBar
                  ? selectedBar.reportingDate
                  : getDefaultDashboardDisplayEndDate()
              }
              onSelectBar={onSelectBar}
            />
          </Box>
          <Flex>
            <Spacer />
            <PortfolioDownloadCsvLink
              dataRows={dataRows}
              headers={headers}
              chartTitle="Funding Overview"
              selectedTimePeriod={selectedTimePeriod}
            />
          </Flex>
        </CardBody>
      </Card>
      {selectedBar && (
        <PortfolioSummaryChartCards
          selectedBarData={selectedBar}
          creditFacilityId={creditFacilityId}
        />
      )}
    </Flex>
  );
};
